import { useQuery } from '@tanstack/react-query';
import { api } from '@xtnd/web/shared/api';
import { templateManagementApi, type TenantTemplatesResponse } from '../../templates/api/templateManagementApi';

export interface ConfiguredTemplates {
  operational?: string;
  operationalReminder?: string;
  evergreen?: string;
  evergreenReminder?: string;
  trustRequest?: string;
  trustRequestReminder?: string;
  trustEstablishment?: string;
  trustEstablishmentReminder?: string;
  midCycleRequest?: string;
  midCycleRequestReminder?: string;
  default?: string;
}

export interface EmailTemplate {
  templateId: string;
  templateName: string;
  templateType: string;
  version: string;
  isActive: boolean;
  description?: string;
  tags?: string[] | string;
  fileSize: number;
  createdAt: string;
  createdBy: string;
}

export interface EmailTemplatesData {
  configured: ConfiguredTemplates;
  allTemplates: EmailTemplate[];
}

/**
 * Returns the configured template ID for a given transaction type and action
 */
export function getDefaultTemplateId(
  configured: ConfiguredTemplates,
  billType: 'operational' | 'evergreen' | 'trust',
  isReminder: boolean
): string | undefined {
  if (billType === 'trust') {
    return isReminder ? configured.trustRequestReminder || configured.trustRequest : configured.trustRequest;
  }
  if (billType === 'evergreen') {
    return isReminder ? configured.evergreenReminder || configured.evergreen : configured.evergreen;
  }
  return isReminder
    ? configured.operationalReminder || configured.operational || configured.default
    : configured.operational || configured.default;
}

/**
 * Juniper config (template ID mappings) - changes infrequently, 30-min staleTime
 */
function useJuniperConfigTemplates(enabled: boolean) {
  return useQuery<ConfiguredTemplates>({
    queryKey: ['juniper', 'config-templates'],
    queryFn: async () => {
      const config: any = await api.get('/juniper/config');
      return {
        operational: config.operationalEmailTemplateId,
        operationalReminder: config.operationalReminderEmailTemplateId,
        evergreen: config.evergreenEmailTemplateId,
        evergreenReminder: config.evergreenReminderEmailTemplateId,
        trustRequest: config.trustRequestEmailTemplateId,
        trustRequestReminder: config.trustRequestReminderEmailTemplateId,
        trustEstablishment: config.trustEstablishmentEmailTemplateId,
        trustEstablishmentReminder: config.trustEstablishmentReminderEmailTemplateId,
        midCycleRequest: config.midCycleRequestEmailTemplateId,
        midCycleRequestReminder: config.midCycleRequestReminderEmailTemplateId,
        default: config.emailTemplateId,
      };
    },
    enabled,
    staleTime: 30 * 60 * 1000, // 30 min - config rarely changes
  });
}

/**
 * All active EMAIL templates - may change more often, 5-min staleTime
 */
function useAllEmailTemplates(enabled: boolean) {
  return useQuery<EmailTemplate[]>({
    queryKey: ['juniper', 'all-email-templates'],
    queryFn: async () => {
      const response: TenantTemplatesResponse = await templateManagementApi.getTenantTemplates('email');
      return (response.templates || []).filter(t => t.isActive);
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

/**
 * Combined hook that fetches both config template mappings and all EMAIL templates.
 * Uses separate queries with different staleTime for optimal caching.
 */
export function useEmailTemplates(enabled = true) {
  const configQuery = useJuniperConfigTemplates(enabled);
  const templatesQuery = useAllEmailTemplates(enabled);

  const isLoading = configQuery.isLoading || templatesQuery.isLoading;

  const data: EmailTemplatesData | undefined =
    configQuery.data && templatesQuery.data
      ? { configured: configQuery.data, allTemplates: templatesQuery.data }
      : undefined;

  return { data, isLoading };
}
